import { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { z } from 'zod';
import type { ModelProvider, ModelProviderSecretScope } from '@reflex/shared';
import {
  MODEL_PROVIDER_LABELS,
  MODEL_PROVIDER_ORDER,
  ModelProviderSecretNameSchema,
  providerSupportsBaseUrl,
} from '@reflex/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { SecretInput } from '@reflex/ui/components/forms/SecretInput';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';

export interface ScopeSelection {
  scope: ModelProviderSecretScope;
  /** Required when `scope === 'team'`; ignored otherwise. */
  teamId?: string;
}

export interface ScopeOptions {
  /** Defaults to `'org'`. */
  defaultScope?: ModelProviderSecretScope;
  /** Scopes to render in the dropdown, in order. */
  allowedScopes: readonly ModelProviderSecretScope[];
  /** Teams the user can save a key under. Empty disables the team option. */
  teams: readonly { id: string; name: string }[];
}

interface AddModelProviderSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called on submit. In add mode `value` is always present. In edit mode
   * (`editSecret` set) `value` may be an empty string, meaning "keep the
   * current value, only the name changed" — the caller decides how to map
   * that into an update patch.
   *
   * `scopeChoice` is only set when `scopeOptions` is provided; otherwise the
   * caller has decided the scope out-of-band (e.g. this dialog is mounted on
   * a scope-specific page).
   */
  onSubmit: (
    provider: ModelProvider,
    name: string,
    value: string,
    scopeChoice?: ScopeSelection,
    /**
     * Custom base URL for providers that support it (Anthropic, OpenAI).
     * `undefined` means "no change / not applicable"; `null` clears a
     * previously-set URL; a string sets it.
     */
    baseUrl?: string | null,
  ) => void;
  /**
   * Enables an inline scope dropdown so the user can save the key at the
   * org, personal (user), or team tier from a single surface. Omit on
   * scope-fixed pages (the dedicated org/team/personal model-providers
   * pages already know their tier from the route).
   */
  scopeOptions?: ScopeOptions;
  /**
   * When set, the dialog opens in edit mode: the provider is locked, the
   * name is prefilled and editable, and the value is optional (blank keeps
   * the existing value). Renaming is the primary reason to keep the value
   * blank.
   */
  editSecret?: { provider: ModelProvider; name: string; baseUrl?: string | null };
  /**
   * Pre-select this provider and lock the provider select when the
   * dialog is opened from a specific provider's "Add key" affordance.
   */
  presetProvider?: ModelProvider;
  /**
   * Names already in use for the selected provider in this scope.
   * Submitting one of these overwrites the existing key in place, so we
   * surface a "replace" hint rather than blocking.
   */
  existingNamesByProvider?: Partial<Record<ModelProvider, readonly string[]>>;
  /** Optional override list. Defaults to {@link MODEL_PROVIDER_ORDER}. */
  providers?: readonly ModelProvider[];
  isPending?: boolean;
  /**
   * Inline validation error from the last save attempt. When set the
   * dialog stays open so the user can correct the key and resubmit.
   */
  errorMessage?: string | null;
}

/**
 * Provider-first add dialog for the model-provider secrets surface. The
 * user picks a provider, types a required name (multiple named keys per
 * provider are supported), and pastes a value. There is no implicit
 * `default` name — every key is explicitly named.
 *
 * The inner form is only mounted while `open` is true, so each re-open
 * re-derives its initial state from the current props without needing a
 * reset effect.
 */
export function AddModelProviderSecretDialog({
  open,
  onOpenChange,
  onSubmit,
  editSecret,
  presetProvider,
  existingNamesByProvider = {},
  providers = MODEL_PROVIDER_ORDER,
  isPending = false,
  errorMessage,
  scopeOptions,
}: AddModelProviderSecretDialogProps) {
  const isEdit = editSecret !== undefined;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Update ${MODEL_PROVIDER_LABELS[editSecret.provider]} API key (${editSecret.name})`
              : 'Add Model Provider Key'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Rename the key and/or replace its value. Leave the value blank to keep the current one.'
              : 'Name the key, pick a provider, and paste the API key. Saved keys are encrypted at rest and made available to agents at launch.'}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <AddModelProviderSecretForm
            editSecret={editSecret}
            presetProvider={editSecret?.provider ?? presetProvider}
            existingNamesByProvider={existingNamesByProvider}
            providers={providers}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
            isPending={isPending}
            errorMessage={errorMessage}
            scopeOptions={isEdit ? undefined : scopeOptions}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const SCOPE_LABELS: Record<ModelProviderSecretScope, string> = {
  org: 'Organization',
  team: 'Team',
  user: 'Personal',
};

const SCOPE_DESCRIPTIONS: Record<ModelProviderSecretScope, string> = {
  org: 'Shared across the whole organization.',
  team: 'Shared with members of the selected team.',
  user: 'Only available to you.',
};

const baseUrlSchema = z.string().url();

function AddModelProviderSecretForm({
  editSecret,
  presetProvider,
  existingNamesByProvider,
  providers,
  onSubmit,
  onCancel,
  isPending,
  errorMessage,
  scopeOptions,
}: {
  editSecret?: { provider: ModelProvider; name: string; baseUrl?: string | null };
  presetProvider?: ModelProvider;
  existingNamesByProvider: Partial<Record<ModelProvider, readonly string[]>>;
  providers: readonly ModelProvider[];
  onSubmit: (
    provider: ModelProvider,
    name: string,
    value: string,
    scopeChoice?: ScopeSelection,
    baseUrl?: string | null,
  ) => void;
  onCancel: () => void;
  isPending: boolean;
  errorMessage?: string | null;
  scopeOptions?: ScopeOptions;
}) {
  const isEdit = editSecret !== undefined;
  // In edit mode the provider is fixed; otherwise honor the preset or fall
  // back to the first available provider.
  const [provider, setProvider] = useState<ModelProvider>(
    editSecret?.provider ?? presetProvider ?? providers[0],
  );
  const [name, setName] = useState(editSecret?.name ?? '');
  const [value, setValue] = useState('');
  const [baseUrl, setBaseUrl] = useState(editSecret?.baseUrl ?? '');
  // Open the advanced section by default when editing a key that already
  // has a custom base URL, so the prefilled value is visible.
  const [showAdvanced, setShowAdvanced] = useState(Boolean(editSecret?.baseUrl));
  const [scope, setScope] = useState<ModelProviderSecretScope>(
    scopeOptions?.defaultScope ?? scopeOptions?.allowedScopes[0] ?? 'org',
  );
  const [teamId, setTeamId] = useState<string>(() => scopeOptions?.teams[0]?.id ?? '');
  const teamRequired = scopeOptions !== undefined && scope === 'team';
  const teamReady = !teamRequired || teamId.length > 0;

  const trimmedName = name.trim();
  const nameResult = ModelProviderSecretNameSchema.safeParse(trimmedName);
  // Only surface the validation message once the user has typed something.
  const nameError =
    trimmedName.length > 0 && !nameResult.success ? nameResult.error.issues[0]?.message : undefined;
  const trimmedValue = value.trim();
  // The advanced Base URL field only applies to providers that expose a
  // base-URL env var (Anthropic, OpenAI). For every other provider the
  // section is hidden and any typed value is ignored.
  const baseUrlSupported = providerSupportsBaseUrl(provider);
  const trimmedBaseUrl = baseUrl.trim();
  const baseUrlError =
    baseUrlSupported && trimmedBaseUrl.length > 0 && !z.validate(baseUrlSchema, trimmedBaseUrl)
      ? 'Enter a valid URL, e.g. https://gateway.example.com'
      : undefined;
  const nameChanged = isEdit && nameResult.success && nameResult.data !== editSecret.name;
  // Treat a changed base URL as an edit too, so renaming is not the only
  // way to submit a base-URL-only change.
  const baseUrlChanged =
    isEdit && baseUrlSupported && trimmedBaseUrl !== (editSecret.baseUrl ?? '');

  /**
   * The base-URL value to hand back on submit. `undefined` when the provider
   * has no base-URL support (omit from the request); otherwise the trimmed URL
   * or `null` to clear it.
   */
  function baseUrlToSubmit(): string | null | undefined {
    if (!baseUrlSupported) return undefined;
    return trimmedBaseUrl.length > 0 ? trimmedBaseUrl : null;
  }
  // The replace hint warns about clobbering a *different* existing key; in
  // edit mode the key's own current name doesn't count as a collision.
  const isReplace =
    (existingNamesByProvider[provider] ?? []).includes(trimmedName) &&
    !(isEdit && trimmedName === editSecret.name);

  // Add requires both a valid name and a value. Edit only needs a valid
  // name plus *some* change — a new value, a rename, or both.
  const canSubmit = isEdit
    ? nameResult.success &&
      (Boolean(trimmedValue) || nameChanged || baseUrlChanged) &&
      !baseUrlError &&
      !isPending
    : nameResult.success && Boolean(trimmedValue) && teamReady && !baseUrlError && !isPending;

  function scopeChoice(): ScopeSelection | undefined {
    if (!scopeOptions) return undefined;
    return scope === 'team' ? { scope, teamId } : { scope };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nameResult.success || baseUrlError) return;
    if (isEdit) {
      // Value may be blank: caller treats "" as "keep current value".
      if (!trimmedValue && !nameChanged && !baseUrlChanged) return;
      onSubmit(provider, nameResult.data, trimmedValue, scopeChoice(), baseUrlToSubmit());
      return;
    }
    if (!trimmedValue || !teamReady) return;
    onSubmit(provider, nameResult.data, trimmedValue, scopeChoice(), baseUrlToSubmit());
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mt-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="model-provider-select">Provider</Label>
          <Select
            value={provider}
            onValueChange={(v) => setProvider(v as ModelProvider)}
            disabled={presetProvider !== undefined}
          >
            <SelectTrigger id="model-provider-select" className="w-full" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p} value={p}>
                  {MODEL_PROVIDER_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {scopeOptions && !isEdit && (
          <div className="space-y-2" data-testid="model-provider-scope-section">
            <Label htmlFor="model-provider-scope">Save to</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as ModelProviderSecretScope)}>
              <SelectTrigger id="model-provider-scope" className="w-full" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.allowedScopes.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    disabled={s === 'team' && scopeOptions.teams.length === 0}
                  >
                    {SCOPE_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{SCOPE_DESCRIPTIONS[scope]}</p>
            {scope === 'team' && scopeOptions.teams.length > 0 && (
              <div className="mt-2 space-y-2">
                <Label htmlFor="model-provider-team">Team</Label>
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger id="model-provider-team" className="w-full" size="sm">
                    <SelectValue placeholder="Select a team…" />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeOptions.teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {scope === 'team' && scopeOptions.teams.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No teams to save to yet. Create a team first, or pick another scope.
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="model-provider-name">Name</Label>
          <Input
            id="model-provider-name"
            placeholder="e.g. personal, billing, team-shared"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            autoFocus
            aria-invalid={nameError !== undefined}
          />
          {nameError ? (
            <p className="text-xs text-destructive">{nameError}</p>
          ) : isReplace ? (
            <p className="text-xs text-muted-foreground">
              A key named "{trimmedName}" already exists for {MODEL_PROVIDER_LABELS[provider]};
              submitting will overwrite it.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="model-provider-value">
            {isEdit ? 'New API key (optional)' : 'API key'}
          </Label>
          <SecretInput
            id="model-provider-value"
            placeholder={
              isEdit ? 'Leave blank to keep the current value…' : 'Paste the provider API key…'
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-invalid={errorMessage ? true : undefined}
            className={cn(errorMessage && 'border-destructive focus-visible:ring-destructive/30')}
          />
        </div>

        {baseUrlSupported && (
          <div className="space-y-2" data-testid="model-provider-advanced-section">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              aria-expanded={showAdvanced}
              aria-controls="model-provider-base-url"
            >
              {showAdvanced ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              Advanced
            </button>
            {showAdvanced && (
              <div className="space-y-2">
                <Label htmlFor="model-provider-base-url">Base URL (optional)</Label>
                <Input
                  id="model-provider-base-url"
                  placeholder="https://api.anthropic.com"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  autoComplete="off"
                  aria-invalid={baseUrlError !== undefined}
                />
                {baseUrlError ? (
                  <p className="text-xs text-destructive">{baseUrlError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Route this key through a custom endpoint. Leave blank to use{' '}
                    {MODEL_PROVIDER_LABELS[provider]}'s default API.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {errorMessage ? (
        <Alert variant="destructive" className="mt-4" data-testid="model-provider-add-error">
          <AlertCircle className="size-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <DialogFooter className="mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit} data-tutorial-id="model-provider-save">
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
